<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { ECOMMERCE_ITEM_PRICE_TYPE, type EcommerceItem } from '@/types/shared/player-config'
import ShopCard from '@/components/ShopCard.vue'
import { useEcommerceItemDetailsPurchase } from '@/composables/game/ecommerce'
import BaseButton from '@/components/base/BaseButton.vue'
import { T } from '@tolgee/vue'
import ThePremium from '@/components/ThePremium.vue'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import type { EcommercePrizeCardItem } from '@/types/shop'
import BoostCard from '@/components/BoostCard.vue'
import { ref } from 'vue'
import { useShop } from '@/composables/game/shop'
import { formatUnshortenedNumber } from '@/helpers/utils'
import DefaultBOGOBadge from '@/components/periodic/default-BOGO/DefaultBOGOBadge.vue'
import DefaultCashbackBadge from '@/components/periodic/default-cashback/DefaultCashbackBadge.vue'
import DefaultBOGXBadge from '@/components/periodic/default-BOGX/DefaultBOGXBadge.vue'
import TonDiscountBadge from '@/components/TonDiscountBadge.vue'

const props = defineProps<{
	show: boolean
	item: EcommerceItem | EcommercePrizeCardItem
}>()
const emit = defineEmits<{
	(e: 'hide'): void
	(e: 'onPurchase'): void
}>()

const {
  starPrice,
  tonPrice,
  isBuyingWithStars,
  isBuyingWithTon,
  showTonPurchase,
  onPurchase,
  selectedPriceType,
} = useEcommerceItemDetailsPurchase(props.item)

const { isDefaultBOGO, isDefaultCashback, isCashback, isBOGO, saleValue, isDefaultBOGX, isBOGX } = useShop()

const isLoading = ref(false)

async function _onPurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE) {
  isLoading.value = true
  try {
    const paid = await onPurchase(priceType)
    if (paid) {
      setTimeout(() => {
        emit('onPurchase')
        isLoading.value = false
      }, priceType === ECOMMERCE_ITEM_PRICE_TYPE.STAR ? 350 : 1)
    } else {
      isLoading.value = false
      selectedPriceType.value = null
    }
  } catch (e) {
    isLoading.value = false
    selectedPriceType.value = null
  }
}

</script>

<template>
  <TheBottomSheet
    :show="show"
    @hide="$emit('hide')"
  >
    <div class="w-full flex flex-col items-center gap-y-12 relative">
      <StarsBalancePill
        class="absolute left-0 -top-6"
      />
      <div class="flex flex-col items-center gap-y-6">
        <div class="w-[88px] h-[116px] relative">
          <DefaultBOGOBadge
            v-if="isDefaultBOGO"
            class="absolute -left-4 -top-5 w-[55px] h-auto z-[3] -rotate-[10deg] overflow-hidden"
          />
          <DefaultBOGXBadge
            v-if="isDefaultBOGX"
            class="absolute -left-4 -top-5 w-[55px] h-auto z-[3] -rotate-[10deg] overflow-hidden"
          />

          <DefaultCashbackBadge
            v-else-if="isDefaultCashback"
            class="absolute -left-4 -top-5 w-[55px] h-auto z-[3] -rotate-[10deg]"
          />
          <BoostCard
            v-if="('timeImage' in item)"
            :item="item as EcommercePrizeCardItem"
            only-visual
          />
          <ShopCard
            v-else
            :item="item"
            :is-buying="false"
            :show-badge="!isCashback && !isBOGO && !isBOGX"
            only-visual
          />
        </div>
        <div class="flex flex-col items-center w-full">
          <div class="flex text-center max-w-[360px] text-2xl font-bold -tracking-[0.48px] text-light-white">
            <span v-if="isBOGO">2x&nbsp;</span>
            <span>
              {{ item.description }} = ${{ starPrice.usdAmount }}
            </span>
          </div>
          <div
            v-if="isCashback && saleValue"
            class="w-full flex items-center text-center justify-center"
          >
            <span class="text-base font-medium text-white/60 whitespace-pre-line text-center">
              <T
                key-name="shop.details.cashback"
                :default-value="`Get {count}% back on your\nDropee Stars balance`"
                :params="{count: saleValue * 100}"
              /> = <ThePremium
                :with-tooltip="false"
                class="size-4 inline"
                image-class="inline"
              /> {{ formatUnshortenedNumber(starPrice.amount * saleValue) }}

            </span>
          </div>
        </div>
      </div>
      <div class="w-full flex flex-col gap-y-3">
        <BaseButton
          v-if="starPrice.amount === 0"
          class="w-full"
          :loading="isBuyingWithStars || (isLoading && selectedPriceType === ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
          :class="(isBuyingWithStars || isLoading) && 'pointer-events -none'"
          @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
        >
          <T
            v-if="isBuyingWithStars || (isLoading && selectedPriceType === ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
            key-name="lingo.giveaway.transaction.loading_cta"
            default-value="Processing..."
          />
          <T
            v-else
            key-name="shop.unlock_for_free"
            default-value="Unlock for free"
          />
        </BaseButton>
        <BaseButton
          v-else
          class="w-full"
          :loading="isBuyingWithStars"
          @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
        >
          <span
            v-if="!isBuyingWithStars"
            class="flex items-center gap-x-2"
          >
            <T
              key-name="starter_pack.buy_with"
              default-value="Buy with"
            />
            <ThePremium :with-tooltip="false" />
            <span class="-ml-0.5">{{ formatUnshortenedNumber(starPrice.amount) }}</span>
          </span>
          <T
            v-else
            key-name="lingo.giveaway.transaction.loading_cta"
            default-value="Processing..."
          />
        </BaseButton>

        <template v-if="showTonPurchase">
          <div class="relative w-full">
            <div class="absolute right-12 -top-1 rotate-[20deg]">
              <TonDiscountBadge label="-10%" />
            </div>
            <BaseButton
              style-type="tertiary"
              :loading="isBuyingWithTon"
              class="relative w-full"
              @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.TON)"
            >
              <span
                v-if="!isBuyingWithTon"
                class="flex items-center gap-x-2"
              >
                <T
                  key-name="starter_pack.buy_with"
                  default-value="Buy with"
                />
                <img
                  src="@/assets/images/ton-symbol.png"
                  class="size-5 min-w-[20px] object-cover"
                >
                <span>{{ tonPrice?.amount }}</span>
              </span>
              <T
                v-else
                key-name="lingo.giveaway.transaction.loading_cta"
                default-value="Processing..."
              />
            </BaseButton>
          </div>

          <div class="mx-auto -mt-1 max-w-[307px] text-center text-sm -tracking-[0.28px] text-white/[.87] font-medium">
            <T
              key-name="transaction.process_description"
              default-value="Transaction may take a few minutes to process. You’ll be notified once it’s successful."
            />
          </div>
        </template>
      </div>
    </div>
  </TheBottomSheet>
</template>
